import { useState, useEffect } from 'react';
import { useStoreState } from 'easy-peasy';
import swal from 'sweetalert';
import { toast } from 'react-toastify';
import client from '../../../../utils/apolloClient';
import {
  fetchCompanyQuery,
  linkPlaceMutation,
} from '../services/companySummaryQueries';

const usePlaceLinking = ({ companyId }) => {
  const { userId } = useStoreState(state => state.auth);

  const [selectedPlace, setSelectedPlace] = useState(null);
  const [linkedPlaces, setLinkedPlaces] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  const fetchLinkedPlaces = async () => {
    if (!companyId) return;
    setLoadingPlaces(true);
    try {
      const { data } = await client.clientPrivate.query({
        query: fetchCompanyQuery,
        variables: { companyId },
        fetchPolicy: 'network-only',
      });
      setLinkedPlaces(data?.fetch_company?.places || []);
    } catch {
      setLinkedPlaces([]);
    } finally {
      setLoadingPlaces(false);
    }
  };

  useEffect(() => {
    fetchLinkedPlaces();
  }, [companyId]);

  const handleLinkPlace = async () => {
    if (!selectedPlace) return;

    const alreadyLinked = linkedPlaces.some(p => p.place_id === selectedPlace.place_id);
    if (alreadyLinked) {
      swal({
        title: 'Already Linked',
        text: `"${selectedPlace.name}" is already linked to this company.`,
        icon: 'error',
      });
      return;
    }

    const confirmed = await swal({
      title: 'Link Place to Company',
      text: `Link "${selectedPlace.name}" to this company?`,
      icon: 'warning',
      buttons: ['Cancel', 'Yes, Link'],
    });
    if (!confirmed) return;

    try {
      setLinking(true);
      const { data } = await client.clientPrivate.mutate({
        mutation: linkPlaceMutation,
        variables: {
          input: { user_id: userId, place_id: selectedPlace.place_id, company_id: companyId },
        },
      });
      const errors = data?.update_place?.error;
      if (errors && errors.length > 0) {
        errors.forEach(e => toast.error(e.description));
      } else {
        toast.success('Place linked to company successfully');
        setSelectedPlace(null);
        setResetKey(k => k + 1);
        fetchLinkedPlaces();
      }
    } catch {
      toast.error('Failed to link place');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkPlace = async place => {
    const confirmed = await swal({
      title: 'Remove Link',
      text: `Remove "${place.name}" from this company?`,
      icon: 'warning',
      buttons: ['Cancel', 'Yes, Remove'],
    });
    if (!confirmed) return;

    try {
      setUnlinkingId(place.place_id);
      const { data } = await client.clientPrivate.mutate({
        mutation: linkPlaceMutation,
        variables: {
          input: { user_id: userId, place_id: place.place_id, company_id: null },
        },
      });
      const errors = data?.update_place?.error;
      if (errors && errors.length > 0) {
        errors.forEach(e => toast.error(e.description));
      } else {
        toast.success('Place unlinked successfully');
        fetchLinkedPlaces();
      }
    } catch {
      toast.error('Failed to unlink place');
    } finally {
      setUnlinkingId(null);
    }
  };

  return {
    selectedPlace,
    setSelectedPlace,
    linkedPlaces,
    loadingPlaces,
    linking,
    unlinkingId,
    resetKey,
    handleLinkPlace,
    handleUnlinkPlace,
  };
};

export default usePlaceLinking;
